import React, { useMemo } from 'react'
// @ts-ignore no types
import { Text } from '@adc/ui-adc'
import {
  Ending,
  getEndTimeMoment,
  getServerTimeMoment,
} from 'common/modules/auctionCountdown'
import { Duration } from 'common/modules/bidBanner/auctionDuration'
import * as blueprint from '@adc/blueprint'
import * as graph from '@adc/sdk'
import type { AuctionBlueprintScheduledOnlineReactiveArgs } from '@adc/blueprint'
import type { FeatureFlags } from '@adc/ui-adc'
import {
  CombinedListingType,
  useListingContext,
  ShortenedOnlineSegmentType as SegmentType,
} from 'common/context/listing'
import PopupTooltip from '../../../components/popupTooltip'
import {
  formatMoneyWithNumeral,
  formatMoneyWithCentsExceptZero,
} from '../../../utils/formatters'
import { shouldShowSealedBidTreatment } from '../../../utils/privateSeller'
import AuctionDetailsTemplate, {
  RegistrationButton,
  EstCreditBidNotExceeded,
  EstCreditBidExceeded,
  ReserveNotSet,
  ReserveNotMet,
  ReserveMet,
  MessageContentContainer,
} from '../template'
import {
  firstLookEndContent,
  firstLookHeaderContent,
} from '../firstLookAuctionDetails'
import SealedBidStatusMessageBox from './sealedBidStatusMessageBox'
import CalendarPopup from './calendar'
import { getERVRow } from './common'

type GetSellerDisclosureValuesFunction =
  NonNullable<SegmentType>['__methods__']['getSellerDisclosureValues']

type SellerDisclosureValues = ReturnType<GetSellerDisclosureValuesFunction>

const segmentHasBids = (
  segment: AuctionBlueprintScheduledOnlineReactiveArgs['segment']
) => segment?.bid_count && segment?.bid_count > 0

interface CounterBidTooltipProps {
  label: string
  tooltip: string
}

// TODO: put in design-system to be used here and in dashboard for data elm id consistency
export const CounterBidTooltip = ({
  label,
  tooltip,
}: CounterBidTooltipProps) => (
  <PopupTooltip
    on='click'
    position='left center'
    elmId='auction_details_counter_bid'
    trigger={
      <Text color='info' size='tiny' className='u-pointer-cursor'>
        {label}
      </Text>
    }
    content={
      <div data-elm-id='auction_details_counter_bid_content'>
        <Text size='tiny' color='white' weight='heavy'>
          {label}
        </Text>
        <Text size='tiny' color='white'>
          {tooltip}
        </Text>
      </div>
    }
  />
)

const additionalContent = (
  shouldShowSealedBidTreatment: boolean,
  listing: CombinedListingType,
  segment: SegmentType
) => {
  if (shouldShowSealedBidTreatment) {
    return <SealedBidStatusMessageBox />
  }

  const ReoReserveSection = () => {
    switch (segment?.reserve_status) {
      case graph.enums.ReserveStatus.ReserveMet:
        return <ReserveMet />
      case graph.enums.ReserveStatus.ReserveNotMet:
        return <ReserveNotMet />
      case graph.enums.ReserveStatus.ReserveNotSet:
        return <ReserveNotSet />
      default:
        return null
    }
  }

  const ForeclosureReserveSection = () => {
    switch (segment?.reserve_status) {
      case graph.enums.ReserveStatus.ReserveMet:
        return <EstCreditBidExceeded />
      case graph.enums.ReserveStatus.ReserveNotMet:
        return <EstCreditBidNotExceeded />
      default:
        return null
    }
  }

  return (
    <div>
      <div className='u-mb-2' data-elm-id='reserve_value'>
        {listing.listing_configuration?.asset_type ===
        graph.enums.AssetType.Foreclosure ? (
          <ForeclosureReserveSection />
        ) : (
          <ReoReserveSection />
        )}
      </div>
    </div>
  )
}

interface DetailContentProps {
  listing: CombinedListingType
  segment: SegmentType
  isInTour?: boolean
  isFirstLookEnabled?: boolean
  isRedesignEnabled?: boolean
  sellerDisclosureValues?: SellerDisclosureValues
}

const detailContent = ({
  isInTour,
  isFirstLookEnabled,
  segment,
  listing,
  isRedesignEnabled,
  sellerDisclosureValues,
}: DetailContentProps) => {
  const details = [
    {
      label: (
        <Text color='gray' data-elm-id='countdown_timer_label'>
          Bidding Ends In
        </Text>
      ),
      value: (
        <Text color='gray' weight='heavy' data-elm-id='countdown_timer_value'>
          <Ending
            endTime={getEndTimeMoment(segment)}
            currentServerTime={getServerTimeMoment(segment)}
          />
        </Text>
      ),
    },
    {
      label: (
        <Text color='gray' data-elm-id='auction_duration_label'>
          Duration
        </Text>
      ),
      value: (
        <Text color='gray' weight='heavy' data-elm-id='auction_duration_value'>
          <Duration
            startDateTime={String(segment.start_date)}
            endDateTime={String(segment.initial_end_date)}
          />
          <CalendarPopup isInTour={isInTour} />
        </Text>
      ),
    },
    {
      // was:
      // !bStatus.hideStartingBid &&
      //   bStatus.showOpeningBid &&
      //   !bStatus.hasBids &&
      label: listing.listing_summary?.show_opening_bid &&
        !segmentHasBids(segment) && (
          <Text color='gray' data-elm-id='opening_bid_label'>
            Opening Bid
          </Text>
        ),
      value: (
        <Text color='gray' weight='heavy' data-elm-id='opening_bid_value'>
          {blueprint.listing.utils.isFclOnlineAuction(listing)
            ? formatMoneyWithCentsExceptZero(segment.starting_bid_amount, 'TBD')
            : formatMoneyWithNumeral(segment.starting_bid_amount, 'TBD')}
        </Text>
      ),
    },
    {
      label: segmentHasBids(segment) && (
        <Text color='gray' data-elm-id='current_bid_label'>
          Current Bid
        </Text>
      ),
      value: (
        <div className='u-pt-2 u-pb-2'>
          <Text color='gray' weight='heavy' data-elm-id='current_bid_value'>
            {formatMoneyWithNumeral(
              segment.current_highest_bid?.bid_amount,
              'TBD'
            )}
          </Text>

          {sellerDisclosureValues?.showSellerDisclosure &&
            sellerDisclosureValues.sellerDisclosureLabel &&
            sellerDisclosureValues.sellerDisclosureTooltipText && (
              <CounterBidTooltip
                label={sellerDisclosureValues.sellerDisclosureLabel}
                tooltip={sellerDisclosureValues.sellerDisclosureTooltipText}
              />
            )}
        </div>
      ),
    },
    {
      label: (
        <Text color='gray' data-elm-id='bid_increment_label'>
          Bid Increment
        </Text>
      ),
      value: (
        <Text color='gray' weight='heavy' data-elm-id='bid_increment_value'>
          {formatMoneyWithNumeral(segment.current_increment_amount, 'TBD')}
        </Text>
      ),
    },
  ]

  if (isFirstLookEnabled) details.splice(2, 0, firstLookEndContent(listing))
  const ervRow = getERVRow(listing, isRedesignEnabled)
  ervRow && details.unshift(ervRow)
  return details
}

interface DuringProps {
  isInTour?: boolean
  featureFlags: FeatureFlags
  isFirstLookEnabled?: boolean
  user?: blueprint.primitives.AuctionUser
  isRedesignEnabled?: boolean
}

export const During = ({
  isInTour,
  featureFlags,
  isFirstLookEnabled,
  user,
  isRedesignEnabled,
}: DuringProps) => {
  const { listing, segment } = useListingContext()

  const shouldShowSealedBidTreatmentForListing = shouldShowSealedBidTreatment({
    segment,
    featureFlags,
  })

  const userPartyId = user?.logged_in && 'party_id' in user ? user.party_id : ''
  const sellerDisclosureValues = useMemo(() => {
    // @ts-ignore allow methods
    if (!userPartyId) return undefined

    return segment?.__methods__.getSellerDisclosureValues(listing, userPartyId)
  }, [listing, segment, userPartyId])

  return (
    <>
      <AuctionDetailsTemplate
        title={
          shouldShowSealedBidTreatmentForListing
            ? ''
            : 'Online Auction - Now Bidding!'
        }
        primaryButton={<RegistrationButton featureFlags={featureFlags} />}
        shouldShowSealedBidTreatment={shouldShowSealedBidTreatmentForListing}
        detailContent={detailContent({
          isInTour,
          isFirstLookEnabled,
          segment,
          listing,
          isRedesignEnabled,
          sellerDisclosureValues,
        })}
        additionalContent={additionalContent(
          shouldShowSealedBidTreatmentForListing,
          listing,
          segment
        )}
        headerContent={isFirstLookEnabled ? firstLookHeaderContent() : null}
        messageContent={
          <MessageContentContainer
            featureFlags={featureFlags}
            enableBidDepositRequiredMessage
            user={user}
          />
        }
      />
    </>
  )
}
